import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { match } from 'ts-pattern';
import { IButton } from '../buttons/button.models.js';
import { CoreComponent } from '../core/core.component.js';
import { FlexRowGap } from '../flex/index.js';
import { IconType } from '../icons/index.js';
import { TagType } from '../tag/index.js';
import { IImage } from '../ui-models.js';

type ExtendedSimpleTableItem = SimpleTableItem & {
	readonly displayTitle: boolean;
	readonly displayDivider: boolean;
};

export type SimpleTableTitleFontType = 'text3' | 'opacity';
export type SimpleTableImage = {
	readonly image: IImage;
	readonly title?: string;
};
export type SimpleTableValueType = 'html' | 'text' | 'tag';

export type SimpleTableItem = {
	readonly valueType: SimpleTableValueType;
	readonly title: string;

	readonly value?: string | number;
	readonly routerLink?: string;
	readonly externalUrl?: string;
	readonly iconType?: IconType;
	readonly hide?: boolean;
	readonly tagType?: TagType;
	readonly images?: readonly SimpleTableImage[];
	readonly videoUrl?: string;
	readonly buttons?: readonly IButton[];
};

@Component({
	selector: 'ui-simple-table',
	templateUrl: './simple-table.component.html',
	styleUrls: ['./simple-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SimpleTableComponent extends CoreComponent {
	public readonly items = input.required<readonly SimpleTableItem[]>();
	public readonly enableDivider = input<boolean>(true);
	public readonly hideEmptyItems = input<boolean>(false);
	public readonly columnsPerRow = input<number>(1);
	public readonly titleFontType = input<SimpleTableTitleFontType>('text3');
	public readonly titleValueRowGap = input<FlexRowGap>();

	public readonly columnsPerRowCalculated = computed<number>(() => {
		if (this.isMobileView()) {
			return 1;
		}

		return this.columnsPerRow();
	});

	public readonly calculatedTitleValueRowGap = computed<FlexRowGap>(() => {
		const customTitleValueRowGap = this.titleValueRowGap();

		if (customTitleValueRowGap) {
			return customTitleValueRowGap;
		}

		return '6px';
	});

	private readonly filteredItems = computed<readonly SimpleTableItem[]>(() => {
		return this.items()
			.filter((m) => !m.hide)
			.filter((m) => {
				if (this.hideEmptyItems()) {
					return m.value !== undefined && m.value !== null && m.value !== '';
				}
				return true;
			});
	});

	public readonly extendedItems = computed<readonly ExtendedSimpleTableItem[]>(() => {
		const pageSize = this.columnsPerRowCalculated();
		const totalPages = Math.ceil(this.filteredItems().length / pageSize);

		return this.filteredItems().map((m, index) => {
			const extendedItem: ExtendedSimpleTableItem = {
				...m,
				displayTitle: this.isMobileView() && m.videoUrl ? false : true,
				displayDivider: match({ index: index, isEnabled: this.enableDivider() ?? false })
					.with({ isEnabled: false }, () => false)
					// hide divider if it's the last row
					.when(
						(m) => Math.floor(m.index / pageSize) < totalPages - 1,
						() => true
					)
					.otherwise(() => false)
			};

			return extendedItem;
		});
	});
}
